import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { domainNamesApi, queryClient } from '~/apis';
import {
  MonitoringFilterProtocolResponse,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCommentUserTypeFilterRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountResponse,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountryFilterRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCustomTagsFilterRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetDomainStatusFilterRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetHeaderCountDataResponse,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailResponse,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListResponse,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailRequest,
  MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailResponse,
} from '~/swagger/data-contracts';
import { domainNamesQueryKeys } from './queryKeys';

export const useCountryFilterQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountryFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    domainNamesQueryKeys.filter.country(request),
    async () => {
      const { data } = await domainNamesApi.domainNameFilterCountryCreate({
        ...request,
      });
      return data;
    },
    {
      ...options,
    }
  );
};

export const useDomainNamesCommentFilterQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCommentUserTypeFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>(
    domainNamesQueryKeys.filter.commentUserType(request),
    async () => {
      const { data } = await domainNamesApi.domainNameFilterCommentUserTypeCreate(request);
      return data;
    },
    {
      ...options,
    }
  );
};

export const useCustomTagsFilterQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCustomTagsFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: domainNamesQueryKeys.filter.customTag(request),
    queryFn: async () => {
      const { data } = await domainNamesApi.domainNameFilterCustomTagsCreate(request);
      return data;
    },
    ...options,
  });
};

export const useDomainNamesCountQuery = (
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountRequest,
  options: UseQueryOptions<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountResponse>
) => {
  return useQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetCountResponse>(
    domainNamesQueryKeys.count(request),
    async () => {
      const { data } = await domainNamesApi.domainNameCountCreate(request);
      return data;
    },
    {
      keepPreviousData: true,
      ...options,
    }
  );
};

export const useDomainNamesListQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListRequest,
  options: UseQueryOptions<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListResponse, unknown, TData>
) => {
  return useQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListResponse, unknown, TData>({
    queryKey: domainNamesQueryKeys.list(request),
    queryFn: async () => {
      const { data } = await domainNamesApi.domainNameGetListCreate(request);
      return data;
    },
    ...options,
  });
};

export const useDomainNamesListInfiniteQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListRequest,
  options: UseInfiniteQueryOptions<
    MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListResponse,
    unknown,
    TData
  >
) => {
  return useInfiniteQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteListResponse, unknown, TData>(
    domainNamesQueryKeys.list(request),
    async ({ pageParam }) => {
      const { data } = await domainNamesApi.domainNameGetListCreate({
        ...request,
        offset: pageParam || request.offset,
      });
      return data;
    },
    {
      ...options,
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.website_list.length < (request.limit || 50)) {
          return;
        }

        return (request.limit || 50) * allPage.length;
      },
    }
  );
};

export const useDomainNamesDetailsQuery = <
  TData = MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailResponse
>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailRequest,
  options: UseQueryOptions<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailResponse, unknown, TData>
) => {
  return useQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteDetailResponse, unknown, TData>({
    queryKey: domainNamesQueryKeys.details(request),
    queryFn: async () => {
      const { data } = await domainNamesApi.domainNameDetailsCreate(request);
      return data;
    },
    ...options,
  });
};

export const useDomainNamesReportedDetailsQuery = <
  TData = MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailResponse
>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailRequest,
  options?: UseQueryOptions<
    MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailResponse,
    unknown,
    TData
  >
) => {
  return useQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetWebsiteReportedDetailResponse, unknown, TData>({
    queryKey: domainNamesQueryKeys.reportedDetails(request),
    queryFn: async () => {
      const { data } = await domainNamesApi.domainNameReportedDetailsCreate(request);
      return data;
    },
    ...options,
  });
};

export const useDomainNamesExportMutation = () => {
  return useMutation(domainNamesApi.domainNameExcelCreate);
};

export const useDomainNamesUpdateStatusMutation = () => {
  return useMutation(domainNamesApi.domainNameStatusTabUpdateCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(domainNamesQueryKeys.all());
    },
  });
};

export const useDomainNamesAddAuthorizedMutation = () => {
  return useMutation(domainNamesApi.domainNameAuthorizedCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(domainNamesQueryKeys.all());
    },
  });
};

export const useDomainNamesCustomTagsApplyMutation = () => {
  return useMutation(domainNamesApi.domainNameCustomTagApplyCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(domainNamesQueryKeys.all());
    },
  });
};
export const useDomainNamesCustomTagsDeleteAllMutation = () => {
  return useMutation(domainNamesApi.domainNameCustomTagDeleteAllCreate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(domainNamesQueryKeys.all());
    },
  });
};

export const useDomainNamesHeaderListQuery = <
  TData = MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetHeaderCountDataResponse
>(
  options?: UseQueryOptions<
    MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetHeaderCountDataResponse,
    unknown,
    TData
  >
) => {
  return useQuery<MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetHeaderCountDataResponse, unknown, TData>(
    domainNamesQueryKeys.headerList(),
    async () => {
      const { data } = await domainNamesApi.domainNameHeaderList();
      return data;
    },
    {
      ...options,
    }
  );
};

export const useDomainStatusFilterQuery = <TData>(
  request: MonitoringWebsiteDomainWebsiteDomainNamesProtocolsGetDomainStatusFilterRequest,
  options: UseQueryOptions<MonitoringFilterProtocolResponse, unknown, TData>
) => {
  return useQuery<MonitoringFilterProtocolResponse, unknown, TData>({
    queryKey: domainNamesQueryKeys.filter.domainStatus(request),
    queryFn: async () => {
      const { data } = await domainNamesApi.domainNameFilterDomainStatusCreate({
        ...request,
      });
      return data;
    },
    ...options,
  });
};
